import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import {
  Box,
  CssBaseline,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { lazy, ReactNode, Suspense, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { PAGES_ROUTES } from '../../../Constants'
import { checkModuleAccess, removeLocalFilters } from '../../../Helpers/Util'
import Header from '../Header'
import { Drawer, DrawerHeader } from './MUI-Drawer-Style'
import './muiDrawer.scss'
import { CustomTooltip } from '../CustomToolTip/CustomToolTip'
import { useUnsavedChanges } from '../../../Context/UnsavedChangesProvider'
import Loader from '../Loader'

const UnsavedPopup = lazy(() => import('../UnsavedPopup'))

export interface LayoutPorpsType {
  children?: ReactNode
  drawerData?: any[]
}

export default function MUIDrawer({ children, drawerData = [] }: LayoutPorpsType) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isDirty, saveDraftCallback, setIsDirty } = useUnsavedChanges()

  const [openUnsavedDialog, setOpenUnsavedDialog] = useState(false)
  const [pendingPath, setPendingPath] = useState<string | null>(null)

  const buildReleaseInfo = {
    build: process.env.REACT_APP_BUILD_VERSION,
    date: process.env.REACT_APP_BUILD_DATE,
  }

  const [sidebarItems, setSidebarItems] = useState<any>([])
  const [open, setOpen] = useState(true)
  const [openNav, setOpenNav] = useState<any>([])

  useEffect(() => {
    activateSelectedRoute(drawerData)
  }, [location.pathname, drawerData])

  const activateSelectedRoute = (drawerDataArgs: any = []) => {
    const openNavCopy: any = []
    const makeActive = (data: any, parentDetails: any = '') => {
      for (const item of data) {
        if (location.pathname.includes(item.path)) {
          item.active = true
          if (parentDetails) {
            parentDetails.active = true
            openNavCopy.push(parentDetails.id)
          }
          openNavCopy.push(item.id)
        } else {
          item.active = false
        }

        if (item.child?.length) {
          makeActive(item.child, item)
        }
      }
    }
    makeActive(drawerDataArgs)
    setOpenNav(openNavCopy)
    setSidebarItems(drawerData)
  }

  const performNavigation = (path: string) => {
    navigate(path)
    removeLocalFilters()
  }

  const handleOnClickItem = (listItemObj: any) => {
    let openNavCopy = [...openNav]
    if (listItemObj.child?.length) {
      if (!open) {
        let path = listItemObj.path

        const moduleAccess = checkModuleAccess(listItemObj?.role)
        if (!moduleAccess.canRead && !moduleAccess.canWrite) {
          path = `${PAGES_ROUTES.AccessDenied}`
        }

        if (isDirty && path !== location.pathname) {
          setPendingPath(path)
          setOpenUnsavedDialog(true)
          return
        }

        performNavigation(path)
        return
      }
      if (openNavCopy.includes(listItemObj.id)) {
        openNavCopy = openNavCopy.filter((item) => item !== listItemObj.id)
      } else {
        openNavCopy.push(listItemObj.id)
      }
      setOpenNav(openNavCopy)
    } else {
      let path = listItemObj.path

      const moduleAccess = checkModuleAccess(listItemObj?.role)
      if (!moduleAccess.canRead && !moduleAccess.canWrite) {
        path = `${PAGES_ROUTES.AccessDenied}`
      }

      if (isDirty && path !== location.pathname) {
        setPendingPath(path)
        setOpenUnsavedDialog(true)
        return
      }

      performNavigation(path)
    }
  }

  const handleConfirmNavigation = () => {
    setOpenUnsavedDialog(false)
    setIsDirty(false)
    if (pendingPath) {
      performNavigation(pendingPath)
      setPendingPath(null)
    }
  }

  const handleCloseUnsavedDialog = () => {
    setOpenUnsavedDialog(false)
    setPendingPath(null)
  }

  return (
    <Box>
      {openUnsavedDialog && (
        <Suspense fallback={<Loader />}>
          <UnsavedPopup
            open={openUnsavedDialog}
            onClose={handleCloseUnsavedDialog}
            onConfirm={async () => {
              await saveDraftCallback?.()
            }}
            onCloseDialog={handleConfirmNavigation}
            hideSaveBtn={!saveDraftCallback}
          />
        </Suspense>
      )}
      <CssBaseline />
      <Header setOpen={setOpen} drawerState={open} />
      <Drawer className='left-sidebar' variant='permanent' open={open}>
        <Divider />
        <List
          className={`font-medium sidebar-list text-inter ${open ? 'open-sidebar' : ''}`}
          style={{ marginTop: '60px' }}
        >
          {sidebarItems.map((listItemObj: any) => (
            <ListItem
              className='text-inter'
              key={listItemObj.id}
              disablePadding
              sx={{ display: 'block' }}
            >
              <DrawerListItem
                listItemObj={listItemObj}
                openNav={openNav}
                onClickItem={() => handleOnClickItem(listItemObj)}
                isChild={false}
                open={open}
              />
              {(openNav || []).includes(listItemObj.id) &&
                open &&
                listItemObj.child?.map((childItemObj: any) => (
                  <DrawerListItem
                    key={childItemObj.id}
                    listItemObj={childItemObj}
                    openNav={openNav}
                    onClickItem={() => handleOnClickItem(childItemObj)}
                    isChild={true}
                    open={open}
                  />
                ))}
            </ListItem>
          ))}
        </List>
        {open && (
          <div className='sidebar-footer-main'>
            <h6>Application Version : {buildReleaseInfo?.build}</h6>
            <p>Last Released on {buildReleaseInfo?.date}</p>
          </div>
        )}
      </Drawer>
      <Box component='main' sx={{ flexGrow: 1, p: 0 }}>
        <Grid
          className={`${open ? 'content-open-layout' : 'content-close-layout'}`}
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 12 }}
        >
          <DrawerHeader />
          <Grid item xs={12}>
            {children}
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export interface ListItemPorpsType {
  listItemObj: any
  openNav?: any[]
  onClickItem?: any
  isChild: boolean
  open: boolean
}

export const DrawerListItem = ({
  listItemObj,
  openNav,
  onClickItem,
  isChild,
  open,
}: ListItemPorpsType) => {
  const isOpen = (openNav || []).includes(listItemObj.id)

  let color = ''
  let backgroundColor = ''
  if (listItemObj.active) {
    color = isChild ? '#ffff' : '#402080'
    backgroundColor = isChild ? '#402080' : '#ECE9F2'
  }

  let borderLeft = ''
  if (!isChild) {
    borderLeft = listItemObj.active ? '2px solid #402080' : '2px solid #ffff'
  }

  return (
    <ListItemButton
      sx={{
        minHeight: '52px',
        display: 'flex',
        alignItems: 'center',
        margin: '10px',
        borderRadius: '4px',
        justifyContent: open ? 'initial' : 'center',
        borderBottom: isChild ? '1px solid #ECE9F2' : '',
        color,
        borderLeft,
        backgroundColor,
        ':hover': { backgroundColor: '#ECE9F2', color: 'black' },
      }}
      className={`text-inter ${listItemObj.active ? 'active' : ''}`}
      onClick={(event) => {
        if (event.ctrlKey || event.metaKey) {
          return
        }
        onClickItem()
      }}
      data-testid={listItemObj.id}
    >
      {listItemObj.icon && (
        <Link to={listItemObj.path}>
          <CustomTooltip
            title={open ? '' : listItemObj.lable}
            placement='right'
            arrow
            disableHoverListener={open}
            disableInteractive
            slotProps={{
              tooltip: {
                sx: {
                  width: '204px',
                  height: '32px',
                  background: '#ECE9F2',
                  color: '#402080',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '14px',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  padding: '4px 12px 4px 12px',
                  left: '7px',
                  '& .MuiTooltip-arrow': {
                    color: '#ECE9F2',
                  },
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 1.5 : 0,
                justifyContent: 'center',
                cursor: 'default',
              }}
            >
              {listItemObj.icon}
            </ListItemIcon>
          </CustomTooltip>
        </Link>
      )}
      {listItemObj.child?.length ? (
        <ListItemText
          primary={
            <div className='d-flex align-items-center justify-content-between'>
              <Typography className='text-inter font-medium'> {listItemObj.lable}</Typography>
              {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </div>
          }
          sx={{ opacity: open ? 1 : 0 }}
        />
      ) : (
        <Link
          to={listItemObj.path}
          state={{ fromPage: listItemObj?.id }}
          style={{
            textDecoration: 'none',
            display: 'contents',
            alignItems: 'center',
            width: '100%',
          }}
          className='table-link'
        >
          <ListItemText
            className='text-inter font-medium'
            primary={
              <Typography className='text-inter font-medium'> {listItemObj.lable}</Typography>
            }
            sx={{ paddingLeft: isChild ? 4 : 0, opacity: open ? 1 : 0 }}
          />
        </Link>
      )}
    </ListItemButton>
  )
}
